import * as turf from "@turf/turf";

// Create marker element with component image
export const createMarkerElement = (
  componentId,
  componentType,
  myComponents
) => {
  // Find the component based on the ID
  const component = myComponents[componentType].find(
    (cmp) => cmp._id === componentId
  );
  const previewUrl = component?.previewUrl;

  // Create a container element
  const el = document.createElement("div");
  el.className = "custom-marker";

  // Style the container
  el.style.width = "40px";
  el.style.height = "40px";
  el.style.borderRadius = "50%";
  el.style.overflow = "hidden";
  el.style.border = "2px solid #d3af5f";
  el.style.backgroundColor = "#ffffff";
  el.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
  el.style.cursor = "pointer";
  el.style.top = "0";
  el.style.left = "0";
  el.style.transform = "translate(-50%, -50%)";
  el.style.marginTop = "0";
  el.style.marginBottom = "0";
  el.style.marginLeft = "0";
  el.style.marginRight = "0";
  el.style.padding = "0";

  // If we have a preview URL, create an image
  if (previewUrl) {
    const img = document.createElement("img");
    img.src = previewUrl;
    img.alt = component?.name || "Component preview";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "cover";
    img.style.display = "block";
    img.style.position = "absolute";
    img.style.top = "0";
    img.style.left = "0";

    // Add error handling to show fallback if image fails to load
    img.onerror = () => {
      el.textContent = component?.name?.[0] || "?";
      el.style.display = "flex";
      el.style.alignItems = "center";
      el.style.justifyContent = "center";
      el.style.fontSize = "16px";
      el.style.fontWeight = "bold";
      el.style.color = "#d3af5f";
    };

    el.appendChild(img);
  } else {
    // No image available, use text instead
    el.textContent = component?.name?.[0] || "?";
    el.style.display = "flex";
    el.style.alignItems = "center";
    el.style.justifyContent = "center";
    el.style.fontSize = "16px";
    el.style.fontWeight = "bold";
    el.style.color = "#d3af5f";
  }

  return el;
};

// Update circle on map
export const updateCircle = (map, circleId, coordinates, radius) => {
  if (!map) return null;

  // Create a proper GeoJSON circle using Turf.js
  // This maintains real-world size regardless of zoom level
  const options = { steps: 64, units: "meters" };
  const circle = turf.circle([coordinates[0], coordinates[1]], radius, options);

  if (map.getSource(circleId)) {
    // Update existing circle source data
    map.getSource(circleId).setData(circle);
  } else {
    // Create new circle
    map.addSource(circleId, {
      type: "geojson",
      data: circle,
    });

    // Add fill layer
    map.addLayer({
      id: `${circleId}-fill`,
      type: "fill",
      source: circleId,
      paint: {
        "fill-color": "#d3af5f",
        "fill-opacity": 0.2,
      },
    });

    // Add outline layer
    map.addLayer({
      id: `${circleId}-border`,
      type: "line",
      source: circleId,
      paint: {
        "line-color": "#d3af5f",
        "line-width": 2,
        "line-opacity": 0.8,
      },
    });
  }

  return circleId;
};

// Remove circle from map
export const removeCircle = (map, circleId) => {
  if (!map) return;

  if (map.getLayer(`${circleId}-fill`)) {
    map.removeLayer(`${circleId}-fill`);
  }
  if (map.getLayer(`${circleId}-border`)) {
    map.removeLayer(`${circleId}-border`);
  }
  if (map.getSource(circleId)) {
    map.removeSource(circleId);
  }
};

// Add custom icon to geocoder
export const addCustomIconToGeocoder = (t, setOpenDrawerMenu) => {
  setTimeout(() => {
    // Find the geocoder input container
    const geocoderInput = document.querySelector(".maplibregl-ctrl-geocoder");
    if (!geocoderInput) return;

    // Check if our custom button already exists
    if (document.querySelector(".geocoder-back-button")) return;

    // Get the input element
    const inputElement = geocoderInput.querySelector(
      ".maplibregl-ctrl-geocoder--input"
    );
    if (inputElement) {
      // Reduce the width of the input to make room for the button
      inputElement.style.width = "calc(100% - 50px)";
      inputElement.style.paddingRight = "40px";
    }

    // Create a hamburger menu button
    const backButton = document.createElement("button");
    backButton.className = "geocoder-back-button";

    // Create hamburger icon SVG
    const svgHamburger = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    svgHamburger.setAttribute("width", "18");
    svgHamburger.setAttribute("height", "18");
    svgHamburger.setAttribute("viewBox", "0 0 24 24");
    svgHamburger.setAttribute("fill", "none");
    svgHamburger.style.display = "block";

    // Create the hamburger icon paths
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z");
    path.setAttribute("fill", "#333333");
    svgHamburger.appendChild(path);

    // Add the SVG to the button
    backButton.appendChild(svgHamburger);

    // Style the button
    backButton.style.position = "absolute";
    backButton.style.right = "8px";
    backButton.style.top = "6px";
    backButton.style.height = "36px";
    backButton.style.width = "36px";
    backButton.style.display = "flex";
    backButton.style.alignItems = "center";
    backButton.style.justifyContent = "center";
    backButton.style.padding = "0";
    backButton.style.cursor = "pointer";
    backButton.style.backgroundColor = "#f1f3f4";
    backButton.style.color = "#333";
    backButton.style.border = "none";
    backButton.style.borderRadius = "4px";
    backButton.style.boxShadow = "0 1px 2px rgba(0,0,0,0.1)";
    backButton.style.zIndex = "5";

    const iconPath = path;

    // Move the close button to make room for our hamburger button
    const closeButton = geocoderInput.querySelector(
      ".maplibregl-ctrl-geocoder--button"
    );
    if (closeButton) {
      closeButton.style.right = "60px";
    }

    // Hover effect - change only the icon to gold
    backButton.onmouseover = () => {
      iconPath.setAttribute("fill", "#d3af5f");
      backButton.style.backgroundColor = "#e5e7e8";
    };
    backButton.onmouseout = () => {
      iconPath.setAttribute("fill", "#333333");
      backButton.style.backgroundColor = "#f1f3f4";
    };

    // Click event to open drawer menu
    backButton.addEventListener("click", () => setOpenDrawerMenu(true));

    // Add button to geocoder container
    geocoderInput.appendChild(backButton);

    // Add helpful instruction caption below geocoder
    const instructionCaption = document.createElement("div");
    instructionCaption.className = "geocoder-instruction";

    // Create info icon SVG
    const infoIcon = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    infoIcon.setAttribute("width", "14");
    infoIcon.setAttribute("height", "14");
    infoIcon.setAttribute("viewBox", "0 0 24 24");
    infoIcon.style.marginRight = "6px";
    infoIcon.style.verticalAlign = "middle";

    // Create the info icon path
    const infoPath = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "path"
    );
    infoPath.setAttribute(
      "d",
      "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
    );
    infoPath.setAttribute("fill", "#5f6368");
    infoIcon.appendChild(infoPath);

    // Create text container for better alignment
    const textSpan = document.createElement("span");
    textSpan.textContent = t("mapWithComponents.addMarkerGeocoderCaption");
    textSpan.style.verticalAlign = "middle";

    // Add the icon and text to the caption
    instructionCaption.appendChild(infoIcon);
    instructionCaption.appendChild(textSpan);

    // Style the instruction caption
    instructionCaption.style.fontSize = "12px";
    instructionCaption.style.color = "#5f6368";
    instructionCaption.style.backgroundColor = "rgba(255, 255, 255, 0.8)";
    instructionCaption.style.padding = "2px 8px";
    instructionCaption.style.borderRadius = "0 0 4px 4px";
    instructionCaption.style.marginTop = "1px";
    instructionCaption.style.marginLeft = "10px";
    instructionCaption.style.width = "calc(100% - 20px)";
    instructionCaption.style.maxWidth = "384px";
    instructionCaption.style.boxShadow = "0 1px 2px rgba(0,0,0,0.1)";
    instructionCaption.style.fontWeight = "500";
    instructionCaption.style.display = "flex";
    instructionCaption.style.alignItems = "center";
    instructionCaption.style.lineHeight = "18px";

    // Insert the caption after the geocoder
    if (geocoderInput.parentNode) {
      geocoderInput.parentNode.insertBefore(
        instructionCaption,
        geocoderInput.nextSibling
      );
    }
  }, 300);
};
